<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header Section -->
    <div class="bg-white border-b border-gray-200">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-semibold text-gray-900">
              Dashboard
            </h1>
            <p class="mt-1 text-sm text-gray-600">
              Manage your connected calendars and integrations
            </p>
          </div>
          <div class="flex items-center space-x-4">
            <a
              href="/google/auth/google_oauth2/initiate"
              class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              v-if="!hasGoogleAccounts">
              <svg class="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              Connect Google Account
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Google Calendars Section -->
      <div class="mb-8">
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-lg font-medium text-gray-900">
            Google Calendars
          </h2>
          <button
            @click="syncCalendars"
            :disabled="syncing"
            class="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            v-if="calendars.length > 0">
            <svg
              class="w-4 h-4 mr-1.5"
              :class="{ 'animate-spin': syncing }"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {{ syncing ? 'Syncing...' : 'Sync Calendars' }}
          </button>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div v-for="n in 3" :key="n" class="bg-white rounded-lg border border-gray-200 p-5 animate-pulse">
            <div class="flex items-start space-x-4">
              <div class="flex-shrink-0">
                <div class="h-10 w-10 bg-gray-200 rounded-lg" />
              </div>
              <div class="flex-1">
                <div class="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                <div class="h-3 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
          </div>
        </div>

        <!-- Calendar Cards -->
        <div v-else-if="calendars.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div
            v-for="calendar in calendars"
            :key="calendar.id"
            class="bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors p-5">
            <div class="flex items-start space-x-4">
              <div class="flex-shrink-0">
                <div class="h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <svg class="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-start justify-between">
                  <div class="flex-1">
                    <h3 class="text-sm font-medium text-gray-900 truncate">
                      {{ calendar.display_name || calendar.summary }}
                    </h3>
                    <p v-if="calendar.description" class="mt-1 text-xs text-gray-500 line-clamp-2">
                      {{ calendar.description }}
                    </p>
                    <div class="mt-2 space-y-1">
                      <div v-if="calendar.timezone" class="flex items-center text-xs text-gray-500">
                        <svg class="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {{ calendar.timezone }}
                      </div>
                      <div v-if="calendar.google_account" class="flex items-center text-xs text-gray-500">
                        <svg class="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {{ calendar.google_account.email }}
                      </div>
                    </div>
                  </div>
                  <div v-if="calendar.primary" class="ml-2">
                    <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                      Primary
                    </span>
                  </div>
                </div>
                <div class="mt-3 flex items-center text-xs text-gray-500">
                  <svg class="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Last synced: {{ formatDate(calendar.last_synced) }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-200">
          <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <h3 class="mt-2 text-sm font-medium text-gray-900">
            No calendars connected
          </h3>
          <p class="mt-1 text-sm text-gray-500">
            Get started by connecting your Google account.
          </p>
          <div class="mt-6">
            <a
              href="/google/auth/google_oauth2/initiate"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
              <svg class="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              Connect Google Account
            </a>
          </div>
        </div>

        <!-- Sync Status Message -->
        <div v-if="syncMessage" class="mt-4">
          <div
            class="rounded-md p-4"
            :class="{
              'bg-green-50 border border-green-200': syncMessage.type === 'success',
              'bg-red-50 border border-red-200': syncMessage.type === 'error',
              'bg-blue-50 border border-blue-200': syncMessage.type === 'info'
            }">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg
                  v-if="syncMessage.type === 'success'"
                  class="h-5 w-5 text-green-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <svg
                  v-else-if="syncMessage.type === 'error'"
                  class="h-5 w-5 text-red-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <svg
                  v-else
                  class="h-5 w-5 text-blue-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div class="ml-3">
                <p
                  class="text-sm font-medium"
                  :class="{
                    'text-green-800': syncMessage.type === 'success',
                    'text-red-800': syncMessage.type === 'error',
                    'text-blue-800': syncMessage.type === 'info'
                  }">
                  {{ syncMessage.text }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { hideLoadingSpinner } from '~/javascript/utils/hide_loader.js'
import { getGoogleCalendars, syncGoogleCalendars } from '~/javascript/api/google_calendars.js'
import { getGoogleAccountsCount } from '~/javascript/api/google_accounts.js'

// Props
// eslint-disable-next-line no-undef
defineProps({
  mapKey: {
    type: String,
    required: false,
    default: ''
  }
})

// State
const calendars = ref([])
const loading = ref(true)
const syncing = ref(false)
const syncMessage = ref(null)
const hasGoogleAccounts = ref(false)

// Helper Methods
const formatDate = (date) => {
  if (!date) return 'Never'
  const d = new Date(date)
  const now = new Date()
  const diffInMinutes = Math.floor((now - d) / 1000 / 60)

  if (diffInMinutes < 1) return 'Just now'
  if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`
  return `${Math.floor(diffInMinutes / 1440)} days ago`
}

const showMessage = (type, text) => {
  syncMessage.value = { type, text }
  setTimeout(() => {
    syncMessage.value = null
  }, 5000)
}

// Methods
const fetchCalendars = async() => {
  try {
    loading.value = true
    const response = await getGoogleCalendars()
    calendars.value = response.data
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching calendars:', error)
    showMessage('error', 'Failed to load calendars. Please try again.')
  } finally {
    loading.value = false
  }
}

const syncCalendars = async() => {
  try {
    syncing.value = true
    syncMessage.value = null

    const response = await syncGoogleCalendars()
    const data = response.data

    if (data.total_synced > 0) {
      showMessage('success', `Successfully synced ${data.total_synced} account(s)`)
      await fetchCalendars() // Refresh the calendar list
    }

    if (data.total_failed > 0) {
      showMessage('error', `Failed to sync ${data.total_failed} account(s)`)
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error syncing calendars:', error)
    showMessage('error', 'Failed to sync calendars. Please try again.')
  } finally {
    syncing.value = false
  }
}

const checkGoogleAccounts = async() => {
  try {
    const response = await getGoogleAccountsCount()
    hasGoogleAccounts.value = response.data.count > 0
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error checking Google accounts:', error)
  }
}

// Lifecycle
onMounted(async() => {
  hideLoadingSpinner()
  await Promise.all([
    fetchCalendars(),
    checkGoogleAccounts()
  ])
})
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
